import { FC, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import {
  Box,
  formattedCurrency,
  Flag,
  GlobalSendRecentTransaction,
  Card,
  withStyles,
  capitalizeFirstLetter,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, UtilButton, ExpandableText, TransactionStatus } from 'UI';
import useTransactionApproval from './useTransactionApproval';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  accountId: string;
};

type TransactionInfoProps = {
  transaction: GlobalSendRecentTransaction;
  notes: string;
};

const CrossBorderWithdrawalInfo: FC<TransactionInfoProps> = ({ transaction, notes }: TransactionInfoProps) => {
  const history = useHistory();
  const toast = useToast();
  const { onSubNavClick } = useNavMenu();
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CrossBorderWithdrawalInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();

  const isApprovalRequested = useMemo(() => {
    return allApprovalTransactions.some((t) => t.ID.toString() === transaction.TransactionID);
  }, [transaction.TransactionID, allApprovalTransactions]);

  useEffect(() => {
    if (!isApprovalRequested) return;

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transaction.TransactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [isApprovalRequested]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDate,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('crossBorderWithdrawal'),
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.totalAmount', { defaultValue: 'Total Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.principalAmount', { defaultValue: 'Principal Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.PrincipalAmount)}`,
    },
    {
      label: t('TransactionDetails.fees', { defaultValue: 'Fee' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(
        String(Number(transaction.Amount) - Number(transaction.PrincipalAmount)),
      )}`,
    },
    {
      label: t('TransactionDetails.customerCreditedAmount', { defaultValue: 'Customer Credited Amount' }),
      value: `${transaction.RecipientCurrency} ${formattedCurrency(transaction.CustomerCreditedAmount)}`,
    },
    {
      label: t('TransactionDetails.fxRate', { defaultValue: 'FX Rate' }),
      value: transaction.FXRate,
    },
    {
      label: t('TransactionDetails.destinationCountry', { defaultValue: 'Destination Country' }),
      value: (
        <Box flx="row" ai="center" jc="fe" gapSize="5px">
          {transaction.DestinationCountryAbbreviation}{' '}
          <Flag flag={transaction.DestinationCountryAbbreviation} width="24px" />
        </Box>
      ),
    },
    {
      label: t('TransactionDetails.deliveryMethod', { defaultValue: 'Delivery Method' }),
      value: transaction.DeliveryMethodType,
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: notes || '-',
    },
  ];

  const approvalHistoryDetails =
    approvalHistory.length > 0
      ? approvalHistory.flatMap((item) => [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: item.Username,
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: (
              <Box flx="col" gapSize="11px" ai="fe">
                <TransactionStatus
                  status={item.ApprovalDecision.toLowerCase()}
                  label={capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                      defaultValue: item.ApprovalDecision,
                    }),
                  )}
                />
                <div>{item.DecisionDate}</div>
              </Box>
            ),
          },
          {
            label: t('ApprovalHistoryDetails.reason', { defaultValue: 'Reason' }),
            value: <ExpandableText text={item.Reason || ''} />,
            visible: item.Reason !== null,
          },
        ])
      : [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: '-',
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: '-',
          },
        ];

  const recipientDetails = [
    {
      label: t('RecipientDetails.name', { defaultValue: 'Name' }),
      value: `${transaction.RecipientFirstName} ${transaction.RecipientLastName}`,
    },
    {
      label: t('RecipientDetails.emailAddress', { defaultValue: 'Email Address' }),
      value: transaction.RecipientEmailAddress || '-',
    },
    {
      label: t('RecipientDetails.address', { defaultValue: 'Address' }),
      value: [transaction.RecipientAddress1, transaction.RecipientAddress2, transaction.RecipientAddressProvince]
        .filter((item) => !!item)
        .join(', '),
    },
    {
      label: t('RecipientDetails.city', { defaultValue: 'City' }),
      value: transaction.RecipientAddressCity,
    },
    {
      label: t('RecipientDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.RecipientAddressPostalCode,
    },
    {
      label: t('RecipientDetails.country', { defaultValue: 'Country' }),
      value: transaction.RecipientAddressCountry,
    },
    {
      label: t('RecipientDetails.primaryContact', { defaultValue: 'Primary Contact' }),
      value: transaction.RecipientPhone,
    },
    {
      label: t('RecipientDetails.businessName', { defaultValue: 'Business Name' }),
      value: transaction.RecipientBusinessName || '-',
    },
    {
      label: t('RecipientDetails.currency', { defaultValue: 'Currency' }),
      value: transaction.RecipientCurrency,
    },
  ];

  const senderDetails = [
    {
      label: t('SenderDetails.name', { defaultValue: 'Name' }),
      value: (
        <>
          {state && state.accountId && state.accountId !== '' ? (
            <UtilButton
              onClick={() => {
                if (clientAccountsEnabled) {
                  history.push(`/client-accounts/${state.accountId}`);
                  onSubNavClick(
                    'clientAccountsList',
                    `/client-accounts/${state.accountId}`,
                    t('accountHolderDetails.clientAccountsList'),
                  );
                } else {
                  history.push(`/contacts/${state.accountId}`);
                  onSubNavClick(
                    'contactAccounts',
                    `/contacts/${state.accountId}`,
                    t('accountHolderDetails.contactList'),
                  );
                }
              }}
            >
              {transaction.SenderFirstName + transaction.SenderLastName}
            </UtilButton>
          ) : (
            <>{transaction.SenderFirstName + transaction.SenderLastName}</>
          )}
        </>
      ),
      visible: !!transaction.SenderFirstName,
    },
    {
      label: t('SenderDetails.emailAddress', { defaultValue: 'EmailAddress' }),
      value: transaction.SenderEmailAddress,
    },
    {
      label: t('SenderDetails.address', { defaultValue: 'Address' }),
      value: [transaction.SenderAddress1, transaction.SenderAddress2, transaction.SenderAddressProvince]
        .filter((item) => !!item)
        .join(', '),
    },
    {
      label: t('SenderDetails.city', { defaultValue: 'City' }),
      value: transaction.SenderAddressCity,
    },
    {
      label: t('SenderDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.SenderAddressPostalCode,
    },
    {
      label: t('SenderDetails.country', { defaultValue: 'Country' }),
      value: transaction.SenderAddressCountry,
    },
    {
      label: t('SenderDetails.purposeOfPayment', { defaultValue: 'Purpose of Payment' }),
      value: transaction.SenderPurposeOfPayment,
    },
    {
      label: t('SenderDetails.businessName', { defaultValue: 'Business Name' }),
      value: transaction.SenderBusinessName || '-',
    },
    {
      label: t('SenderDetails.currency', { defaultValue: 'Currency' }),
      value: transaction.SenderCurrency,
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.TransactionDate,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={transaction.FirstName + ' ' + transaction.LastName}
        amount={transaction.Amount}
        currency={transaction.SenderCurrency}
        transactionId={transaction.TransactionID}
        status={transaction.Status}
        type={'CrossBorder Withdrawal'}
        transactionDate={''}
        lastModified={transaction.TransactionDate}
        failureReason={transaction.FailureReason}
        isApprovalRequested={isApprovalRequested}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {isApprovalRequested && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('ApprovalHistoryDetails.title', { defaultValue: 'Approval History' })}
          </Title>
          <DetailsRows className={cn('rows')} details={approvalHistoryDetails} />
        </Card>
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('senderDetailsTitle', { defaultValue: 'Sender Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={senderDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientDetailsTitle', { defaultValue: 'Recipient Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default CrossBorderWithdrawalInfo;
